import { useState } from "react";

import { Box, Button, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, IconButton, Paper, Switch, Typography } from "@mui/material"
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { DataLayout, FieldControl } from "../../../shared/components"
import { useAdd, useFetchReminder, useUpdate } from "../hooks/useReminder";
import { formatDate } from "../../../shared/utils/formatDate";

export const Reminder = ({ jeopardyId }: { jeopardyId: string | undefined }) => {

    const [reminder, setReminder] = useState({
        date: "",
        message: "",
        isCompleted: false,
    });

    const [isCompleted, setIsCompleted] = useState(false);
    const { data, isLoading, isError, error, refetch } = useFetchReminder(jeopardyId);

    const add = useAdd();
    const edit = useUpdate();

    const [isEditing, setIsEditing] = useState<{
        status: boolean;
        id: string | null;
    }>({
        status: false,
        id: null
    });

    const [isMinimized, setIsMinimized] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleSubmit = async () => {
        setIsSaving(true);
        try {
            if (isEditing && isEditing.status && isEditing.id) {
                const payload = {
                    "reminder_id": isEditing.id,
                    "reminder_title": reminder.message,
                    "reminder_date": reminder.date,
                    "is_completed": reminder.isCompleted,
                };
                await edit(payload);
            } else {
                const payload = {
                    "jeopardy_id": jeopardyId || "",
                    "reminder_title": reminder.message,
                    "reminder_date": reminder.date,
                };
                await add(payload);
            }
            setReminder({ date: "", message: "", isCompleted: false });
            setIsEditing({ status: false, id: null });
            refetch();
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        );
    };

    if (isError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <Typography variant="h6" color="error">
                    Error loading reminders
                </Typography>
            </Box>
        );
    };

    return (
        <Box p={2} display={'flex'} flexDirection={'column'} gap={2} height={'100%'} overflow={'hidden'}>

            <Paper
                data-background-elevation={1}
                variant="outlined"
                sx={{ overflow: "hidden", width: "100%", flex: 1, display: 'flex', flexDirection: 'column', }}
            >
                <Box
                    bgcolor={"filled"}
                    sx={{
                        px: 1.5,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "action.hover",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="subtitle2">Reminders</Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isCompleted}
                                onChange={(e) => setIsCompleted(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Completed"
                    />
                </Box>
                <Box display={'flex'} flexDirection={'column'} p={2} gap={2} sx={{
                    overflowY: 'auto',
                    mr: .5
                }}>
                    {
                        isCompleted ?
                            data.filter((reminderItem: any) => reminderItem.is_completed).map((reminderItem: any) => (
                                <Paper elevation={1} key={reminderItem.id} variant="outlined">
                                    <Box key={reminderItem.id} px={1.5} py={1} borderRadius={1} display={'flex'} justifyContent={'space-between'} gap={1}>
                                        <Box display={'flex'} flexDirection={'column'} gap={.5}>
                                            <Typography variant="subtitle2">{reminderItem.reminder_title}</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatDate(reminderItem.reminder_date)}</Typography>
                                        </Box>
                                        <FormControlLabel
                                            sx={{ mx: 0 }}
                                            control={
                                                <Checkbox
                                                    checked={reminderItem.is_completed}
                                                    onChange={async () => {
                                                        const payload = {
                                                            "reminder_id": reminderItem.id,
                                                            "reminder_title": reminderItem.reminder_title,
                                                            "reminder_date": reminderItem.reminder_date,
                                                            "is_completed": !reminderItem.is_completed,
                                                        };
                                                        await edit(payload);
                                                        refetch();
                                                    }}
                                                    color="primary"
                                                />
                                            }
                                            label=""
                                        />
                                    </Box>
                                </Paper>
                            )) :
                            data.filter((reminderItem: any) => !reminderItem.is_completed).map((reminderItem: any) => (
                                <Paper elevation={1} key={reminderItem.id} variant="outlined">
                                    <Box key={reminderItem.id} px={1.5} py={1} borderRadius={1} display={'flex'} justifyContent={'space-between'} gap={1}>
                                        <Box display={'flex'} flexDirection={'column'} gap={.5}>
                                            <Typography variant="subtitle2">{reminderItem.reminder_title}</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatDate(reminderItem.reminder_date)}</Typography>
                                        </Box>
                                        <FormControlLabel
                                            sx={{ mx: 0 }}
                                            control={
                                                <Checkbox
                                                    checked={reminderItem.is_completed}
                                                    onChange={async () => {
                                                        const payload = {
                                                            "reminder_id": reminderItem.id,
                                                            "reminder_title": reminderItem.reminder_title,
                                                            "reminder_date": reminderItem.reminder_date,
                                                            "is_completed": !reminderItem.is_completed,
                                                        };
                                                        await edit(payload);
                                                        refetch();
                                                    }}
                                                    color="primary"
                                                />
                                            }
                                            label=""
                                        />
                                    </Box>
                                </Paper>
                            ))
                    }
                </Box>
            </Paper>

            <Box flexShrink={1}>
                <DataLayout title="Add Reminder" action={<IconButton onClick={() => setIsMinimized(!isMinimized)}>{isMinimized ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>}>
                    <Box display={'flex'} flexDirection={'column'} gap={2} p={2} style={{ display: isMinimized ? 'none' : 'flex' }}>
                        <FieldControl
                            keyName={"message"}
                            label={"Reminder Message"}
                            variant={"input"}
                            onChange={(_key: string, value: any) => setReminder({ ...reminder, message: value })}
                            value={reminder.message}
                            disabled={isEditing.status}
                        />
                        <FieldControl
                            keyName={"date"}
                            label={"Reminder Date"}
                            variant={"date"}
                            onChange={(_key: string, value: any) => setReminder({ ...reminder, date: value })}
                            value={reminder.date}
                            disabled={isEditing.status}
                        />
                        <Box display={'flex'} gap={1}>
                            <Chip label="Today" variant="outlined" />
                            <Chip label="Tomorrow" variant="outlined" />
                        </Box>
                        <Divider />
                        <Box display={'flex'} justifyContent={'flex-end'} gap={2}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setReminder({ date: "", message: "", isCompleted: false });
                                    setIsEditing({ status: false, id: null });
                                }}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={isSaving || !reminder.message.trim() || !reminder.date}
                                startIcon={isSaving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : undefined}
                            >
                                {isSaving ? "Saving..." : "Save Reminder"}
                            </Button>
                        </Box>
                    </Box>
                </DataLayout>
            </Box>

        </Box>
    )
}